/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Vacations Contract - oRPC Migration
 *
 * This contract defines all vacation-related API endpoints for type-safe
 * client-server communication using oRPC.
 *
 * Endpoints:
 * - GET    /api/vacations        - List all vacation periods
 * - POST   /api/vacations/update - Update/replace vacation periods
 * - POST   /api/vacations/undo   - Restore/undo vacation periods
 * - DELETE /api/vacations/{id}   - Delete a specific vacation period
 */

import { oc } from "@orpc/contract";
import * as z from "zod";
import { commonErrors } from "./base";

// =============================================================================
// Schemas
// =============================================================================

/**
 * Vacation period schema - represents a date range when events are blocked.
 * Dates are in ISO format (YYYY-MM-DD).
 */
export const VacationPeriodSchema = z.object({
	id: z.string(),
	startDate: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format"),
	endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format"),
	note: z.string().trim().nullish(),
});

/**
 * Input schema for creating/updating vacation periods.
 * ID is optional (auto-generated for new periods).
 */
export const VacationPeriodInputSchema = z.object({
	id: z.string().nullish(),
	startDate: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format"),
	endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format"),
	note: z.string().trim().nullish(),
});

/**
 * Standard success response with message.
 */
export const VacationSuccessResponseSchema = z.object({
	message: z.string(),
});

// =============================================================================
// Error Definitions
// =============================================================================

const vacationErrors = {
	UNAUTHORIZED: commonErrors.UNAUTHORIZED,
	NOT_FOUND: {
		message: "Vacation period not found",
		data: z.object({ id: z.string() }),
	},
	VALIDATION_ERROR: {
		message: "Invalid vacation period data",
		data: z.object({
			errors: z.array(
				z.object({
					path: z.string(),
					message: z.string(),
				})
			),
		}),
	},
	DATABASE_ERROR: {
		message: "Database operation failed",
		data: z.object({
			operation: z.enum(["list", "update", "restore", "delete"]),
		}),
	},
};

// =============================================================================
// Contract
// =============================================================================

export const vacationsContract = {
	/**
	 * List all vacation periods
	 * GET /api/vacations
	 */
	list: oc
		.route({
			method: "GET",
			path: "/api/vacations",
			summary: "List vacation periods",
			description: "Get all configured vacation periods that block events.",
			tags: ["Vacations"],
		})
		.input(z.object({}))
		.output(z.array(VacationPeriodSchema))
		.errors({
			UNAUTHORIZED: vacationErrors.UNAUTHORIZED,
			DATABASE_ERROR: {
				message: "Failed to retrieve vacation periods",
				data: z.object({
					operation: z.literal("list"),
				}),
			},
		}),

	/**
	 * Update/replace all vacation periods
	 * POST /api/vacations/update
	 */
	update: oc
		.route({
			method: "POST",
			path: "/api/vacations/update",
			summary: "Update vacation periods",
			description:
				"Update or add vacation periods that will block events during those dates. Replaces all existing periods.",
			tags: ["Vacations"],
		})
		.input(
			z.object({
				periods: z.array(VacationPeriodInputSchema),
				source: z.string().optional(),
			})
		)
		.output(VacationSuccessResponseSchema)
		.errors({
			UNAUTHORIZED: vacationErrors.UNAUTHORIZED,
			VALIDATION_ERROR: vacationErrors.VALIDATION_ERROR,
			DATABASE_ERROR: {
				message: "Failed to update vacation periods",
				data: z.object({
					operation: z.literal("update"),
				}),
			},
		}),

	/**
	 * Restore/undo vacation periods
	 * POST /api/vacations/undo
	 */
	restore: oc
		.route({
			method: "POST",
			path: "/api/vacations/undo",
			summary: "Restore vacation periods",
			description:
				"Remove vacation periods, restoring normal event availability. Used for undo operations.",
			tags: ["Vacations"],
		})
		.input(
			z.object({
				periods: z.array(VacationPeriodInputSchema),
				source: z.string().optional(),
			})
		)
		.output(VacationSuccessResponseSchema)
		.errors({
			UNAUTHORIZED: vacationErrors.UNAUTHORIZED,
			VALIDATION_ERROR: vacationErrors.VALIDATION_ERROR,
			UNDO_FAILED: {
				message: "Failed to undo vacation update",
				data: z.object({
					reason: z.string().optional(),
				}),
			},
			DATABASE_ERROR: {
				message: "Failed to restore vacation periods",
				data: z.object({
					operation: z.literal("restore"),
				}),
			},
		}),

	/**
	 * Delete a specific vacation period
	 * DELETE /api/vacations/{id}
	 */
	delete: oc
		.route({
			method: "DELETE",
			path: "/api/vacations/{id}",
			summary: "Delete vacation period",
			description: "Delete a specific vacation period by ID.",
			tags: ["Vacations"],
		})
		.input(
			z.object({
				id: z.string(), // UUID string, no coercion needed
				source: z.string().optional(),
			})
		)
		.output(VacationSuccessResponseSchema)
		.errors({
			UNAUTHORIZED: vacationErrors.UNAUTHORIZED,
			NOT_FOUND: vacationErrors.NOT_FOUND,
			DATABASE_ERROR: {
				message: "Failed to delete vacation period",
				data: z.object({
					operation: z.literal("delete"),
				}),
			},
		}),
};

// =============================================================================
// Type Exports
// =============================================================================

import type {
	InferContractRouterInputs,
	InferContractRouterOutputs,
} from "@orpc/contract";

export type VacationsContract = typeof vacationsContract;

// Schema-derived types
export type VacationPeriod = z.infer<typeof VacationPeriodSchema>;
export type VacationPeriodInput = z.infer<typeof VacationPeriodInputSchema>;
export type VacationSuccessResponse = z.infer<
	typeof VacationSuccessResponseSchema
>;

// Contract input/output types using oRPC inference utilities
type VacationsInputs = InferContractRouterInputs<VacationsContract>;
type VacationsOutputs = InferContractRouterOutputs<VacationsContract>;

// Input types
export type ListVacationsInput = VacationsInputs["list"];
export type UpdateVacationsInput = VacationsInputs["update"];
export type RestoreVacationsInput = VacationsInputs["restore"];
export type DeleteVacationInput = VacationsInputs["delete"];

// Output types
export type ListVacationsOutput = VacationsOutputs["list"];
export type UpdateVacationsOutput = VacationsOutputs["update"];
export type RestoreVacationsOutput = VacationsOutputs["restore"];
export type DeleteVacationOutput = VacationsOutputs["delete"];
